// 🐛 DEBUG SERVICE - Controls console.log messages based on settings
// This service replaces direct console.log calls to allow ON/OFF control

class DebugService {
  constructor() {
    this.debugEnabled = false;  // Default to OFF
    this.isInitialized = false;
    this.pendingLogs = [];      // Store logs until settings are loaded
  }

  // Initialize debug service with settings from API
  async initialize() {
    try {
      // Get debug setting from settings API
      const response = await fetch('/api/bot-settings/status');
      const data = await response.json();
      
      this.debugEnabled = data.settings?.debug_enabled || false;
      this.isInitialized = true;
      
      console.log(`🐛 Debug Service initialized - Debug ${this.debugEnabled ? 'ENABLED' : 'DISABLED'}`);
      
      // Process any pending logs that were queued before initialization
      this.processPendingLogs();
      
    } catch (error) {
      console.warn('⚠️ Debug Service initialization failed, defaulting to OFF:', error.message);
      this.debugEnabled = false;
      this.isInitialized = true;
    }
  }

  // Process logs that were queued before settings were loaded
  processPendingLogs() {
    if (this.debugEnabled && this.pendingLogs.length > 0) {
      console.log(`🐛 Processing ${this.pendingLogs.length} pending debug logs:`);
      this.pendingLogs.forEach(({ level, args }) => {
        console[level](...args);
      });
    }
    // Clear pending logs
    this.pendingLogs = [];
  }

  // Update debug setting (called when user changes setting)
  updateSetting(enabled) {
    const wasEnabled = this.debugEnabled;
    this.debugEnabled = enabled;
    
    console.log(`🐛 Debug Service updated: ${wasEnabled ? 'ON' : 'OFF'} → ${enabled ? 'ON' : 'OFF'}`);
    
    if (enabled && !wasEnabled) {
      console.log('🐛 Debug logging is now ENABLED - console messages will appear');
    } else if (!enabled && wasEnabled) {
      console.log('🐛 Debug logging is now DISABLED - console messages will be suppressed');
    }
  }

  // Main debug log function (replaces console.log)
  log(...args) {
    if (!this.isInitialized) {
      // Queue logs until settings are loaded
      this.pendingLogs.push({ level: 'log', args });
      return;
    }

    if (this.debugEnabled) {
      console.log(...args);
    }
  }

  // Debug warn function (replaces console.warn)
  warn(...args) {
    if (!this.isInitialized) {
      this.pendingLogs.push({ level: 'warn', args });
      return;
    }

    if (this.debugEnabled) {
      console.warn(...args);
    }
  }

  // Debug error function (replaces console.error)
  error(...args) {
    if (!this.isInitialized) {
      this.pendingLogs.push({ level: 'error', args });
      return;
    }

    if (this.debugEnabled) {
      console.error(...args);
    }
  }

  // Debug info function (replaces console.info)
  info(...args) {
    if (!this.isInitialized) {
      this.pendingLogs.push({ level: 'info', args });
      return;
    }

    if (this.debugEnabled) {
      console.info(...args);
    }
  }

  // Force log (always shows regardless of debug setting) - for critical messages
  forceLog(...args) {
    console.log(...args);
  }

  // Force error (always shows regardless of debug setting) - for critical errors  
  forceError(...args) {
    console.error(...args);
  }

  // Get current debug status
  isEnabled() {
    return this.debugEnabled;
  }

  // Get debug statistics
  getStats() {
    return {
      enabled: this.debugEnabled,
      initialized: this.isInitialized,
      pendingLogs: this.pendingLogs.length
    };
  }
}

// Create and export singleton instance
const debugService = new DebugService();

// Initialize debug service when module is imported
debugService.initialize();

export default debugService;
